import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API, BACKEND_URL } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { HeartPulse, FileDown, ShieldCheck } from "lucide-react";

export default function SharedReport() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${API}/reports/share/${token}`).then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.detail || "Link not available"));
  }, [token]);

  if (err) return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[#F9F8F6]">
      <Card className="p-8 rounded-2xl max-w-md text-center">
        <h2 className="font-heading text-2xl font-medium">Link unavailable</h2>
        <p className="text-[#5C6D63] mt-2">{err}</p>
      </Card>
    </div>
  );

  if (!data) return <div className="min-h-screen flex items-center justify-center text-[#7D8F84]">Loading…</div>;

  const name = (data.member && data.member.name) || (data.user && data.user.name) || "Patient";
  const pdfUrl = `${API}/reports/share/${token}/pdf`;

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <header className="bg-white/90 backdrop-blur-md border-b border-[#E5E7E4]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <HeartPulse className="w-6 h-6 text-[#4A6750]" strokeWidth={1.5} />
            <span className="font-heading text-lg font-medium tracking-tight">Verda Health</span>
          </div>
          <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
            <Button data-testid="shared-pdf-btn" className="rounded-xl bg-[#4A6750] hover:bg-[#3B5340]">
              <FileDown className="w-4 h-4 mr-2" /> Download PDF
            </Button>
          </a>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6 fade-in">
        <div>
          <p className="text-xs font-semibold tracking-[0.15em] uppercase text-[#7D8F84]">Shared Health Summary</p>
          <h1 className="font-heading text-4xl font-semibold tracking-tight mt-1">{name}</h1>
          <p className="text-[#5C6D63] mt-1">{data.start} → {data.end}</p>
          <p className="text-xs text-[#7D8F84] mt-2 flex items-center gap-1.5">
            <ShieldCheck className="w-3 h-3" /> Patient-generated. Read-only. Not a medical diagnosis.
          </p>
        </div>

        {data.member && (
          <Card className="p-6 rounded-2xl border border-[#E5E7E4]">
            <h3 className="font-heading text-lg font-medium mb-3">Profile</h3>
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <Field k="Relation" v={data.member.relation} />
              <Field k="Age" v={data.member.age} />
              <Field k="Gender" v={data.member.gender} />
              <Field k="Blood group" v={data.member.blood_group} />
              {data.member.address && <div className="col-span-full"><Field k="Address" v={data.member.address} /></div>}
            </dl>
          </Card>
        )}

        <Section title={`Vitals (${data.vitals.length})`}>
          {data.vitals.length === 0 ? <Empty /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs uppercase tracking-wider text-[#7D8F84]">
                  <th className="py-2 pr-4">Date</th><th className="py-2 pr-4">BP</th>
                  <th className="py-2 pr-4">Sugar</th><th className="py-2 pr-4">HR</th>
                  <th className="py-2 pr-4">Weight</th><th className="py-2 pr-4">SpO₂</th>
                  <th className="py-2 pr-4">Temp</th>
                </tr></thead>
                <tbody>{data.vitals.map(v => (
                  <tr key={v.id} className="border-t border-[#E5E7E4]">
                    <td className="py-2 pr-4">{v.date}</td>
                    <td className="py-2 pr-4">{(v.bp_systolic && v.bp_diastolic) ? `${v.bp_systolic}/${v.bp_diastolic}` : "—"}</td>
                    <td className="py-2 pr-4">{v.sugar || "—"}</td>
                    <td className="py-2 pr-4">{v.heart_rate || "—"}</td>
                    <td className="py-2 pr-4">{v.weight || "—"}</td>
                    <td className="py-2 pr-4">{v.oxygen || "—"}</td>
                    <td className="py-2 pr-4">{v.temperature || "—"}</td>
                  </tr>))}</tbody>
              </table>
            </div>
          )}
        </Section>

        <Section title={`Symptoms (${data.symptoms.length})`}>
          {data.symptoms.length === 0 ? <Empty /> : data.symptoms.map(s => (
            <div key={s.id} className="py-2 border-b border-[#E5E7E4] last:border-b-0">
              <p className="text-xs text-[#7D8F84]">{s.date} · {s.severity}</p>
              <p className="font-medium">{s.symptom}</p>
              {s.notes && <p className="text-sm text-[#5C6D63] italic">{s.notes}</p>}
            </div>
          ))}
        </Section>

        <Section title={`Diagnostics (${data.diagnostics.length})`}>
          {data.diagnostics.length === 0 ? <Empty /> : data.diagnostics.map(d => (
            <div key={d.id} className="py-3 border-b border-[#E5E7E4] last:border-b-0">
              <p className="text-xs text-[#7D8F84]">{d.date}{d.lab_name ? ` · ${d.lab_name}` : ""}</p>
              <p className="font-medium">{d.type}</p>
              {d.parameters?.length > 0 && (
                <table className="w-full text-xs mt-2">
                  <tbody>{d.parameters.map((p, i) => (
                    <tr key={`${p.name || "p"}-${i}`} className="border-t border-[#E5E7E4]">
                      <td className="py-1 pr-3">{p.name}</td>
                      <td className="py-1 pr-3">{p.value} {p.unit}</td>
                      <td className="py-1 pr-3 text-[#7D8F84]">{p.reference}</td>
                      <td className="py-1"><span className="text-xs">{p.flag || "normal"}</span></td>
                    </tr>))}</tbody>
                </table>
              )}
            </div>
          ))}
        </Section>

        <Section title={`Consultations (${data.consultations.length})`}>
          {data.consultations.length === 0 ? <Empty /> : data.consultations.map(c => (
            <div key={c.id} className="py-3 border-b border-[#E5E7E4] last:border-b-0">
              <p className="text-xs text-[#7D8F84]">{c.date}</p>
              <p className="font-medium">Dr. {c.doctor_name}{c.specialization ? ` · ${c.specialization}` : ""}</p>
              {c.hospital && <p className="text-sm text-[#5C6D63]">{c.hospital}</p>}
              {c.diagnosis && <p className="text-sm mt-1"><span className="text-[#7D8F84]">Diagnosis:</span> {c.diagnosis}</p>}
              {c.notes && <p className="text-sm text-[#5C6D63] italic mt-1">{c.notes}</p>}
            </div>
          ))}
        </Section>

        <Section title={`Medications (${data.medications.length})`}>
          {data.medications.length === 0 ? <Empty /> : data.medications.map(m => {
            const slots = ["morning","afternoon","evening","night"].filter(k => m[k]);
            return (
              <div key={m.id} className="py-3 border-b border-[#E5E7E4] last:border-b-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <p className="font-medium">{m.name}</p>
                  {m.dosage && <span className="text-sm text-[#5C6D63]">· {m.dosage}</span>}
                </div>
                <p className="text-sm text-[#5C6D63] mt-1">
                  {slots.length ? slots.join(" · ") : "—"} {m.food_timing ? `· ${m.food_timing} food` : ""}
                </p>
              </div>
            );
          })}
        </Section>
      </main>
    </div>
  );
}

const Field = ({ k, v }) => (
  <div><p className="text-xs uppercase tracking-wider text-[#7D8F84]">{k}</p><p className="text-[#1C2E24] mt-0.5">{v || "—"}</p></div>
);
const Section = ({ title, children }) => (
  <Card className="p-6 rounded-2xl border border-[#E5E7E4]">
    <h3 className="font-heading text-xl font-medium mb-3">{title}</h3>
    {children}
  </Card>
);
const Empty = () => <p className="text-sm text-[#7D8F84] italic">No entries.</p>;
